import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

interface ModuleConfig {
  homeRoute: string;
  displayName: string;
}

@Component({
  selector: 'app-contextual-not-found',
  templateUrl: './contextual-not-found.component.html',
  styleUrls: ['./contextual-not-found.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ContextualNotFoundComponent implements OnInit {
  currentModule: string = '';
  homeRoute: string = '/analytics/home';
  displayName: string = 'Analytics';

  private moduleConfigs: Record<string, ModuleConfig> = {
    admin: {
      homeRoute: '/admin',
      displayName: 'Admin Home',
    },
    connector: {
      homeRoute: '/analytics/connectors',
      displayName: 'Connectors',
    },
    presentation: {
      homeRoute: '/presentation',
      displayName: 'Presentation',
    },
    'source-catalog': {
      homeRoute: '/source-catalog',
      displayName: 'Source Catalog Home',
    },
    auth: {
      homeRoute: '/auth/login',
      displayName: 'Login',
    },
    analytics: {
      homeRoute: '/analytics/home',
      displayName: 'Analytics Home',
    },
  };

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.detectCurrentModule();
  }

  private detectCurrentModule(): void {
    const currentUrl = this.router.url;

    const urlSegments = currentUrl.split('/').filter((segment) => segment);

    if (urlSegments.length > 0) {
      const firstSegment = urlSegments[0];

      // * check if it's a known Module
      if (this.moduleConfigs[firstSegment]) {
        this.currentModule = firstSegment;
        this.homeRoute = this.moduleConfigs[firstSegment].homeRoute;
        this.displayName = this.moduleConfigs[firstSegment].displayName;
      } else {
        this.handleAnalyticsModule(currentUrl);
      }
    } else {
      // ! default to Analytics for root-level invalid Routes
      this.setDefaultModule();
    }
  }

  private handleAnalyticsModule(url: string): void {
    // ! check if URL starts with any Analytics-related paths
    const analyticsRoutes = [
      '/analytics',
      '/home',
      '/feeds',
      '/dashboards',
      '/alerts',
      '/workspace',
    ];
    const isAnalyticsRoute = analyticsRoutes.some((route) =>
      url.startsWith(route),
    );

    if (isAnalyticsRoute || this.isInCoreModule()) {
      this.currentModule = 'analytics';
      this.homeRoute = this.moduleConfigs['analytics'].homeRoute;
      this.displayName = this.moduleConfigs['analytics'].displayName;
    } else {
      this.setDefaultModule();
    }
  }

  private isInCoreModule(): boolean {
    let route = this.activatedRoute;
    while (route.parent) {
      route = route.parent;
      if (
        route.snapshot.data &&
        route.snapshot.data['section'] === 'analytics'
      ) {
        return true;
      }
    }
    return false;
  }

  private setDefaultModule(): void {
    this.currentModule = 'analytics';
    this.homeRoute = this.moduleConfigs['analytics'].homeRoute;
    this.displayName = this.moduleConfigs['analytics'].displayName;
  }

  goHome(): void {
    this.router.navigate([this.homeRoute]);
  }

  goToMainDashboard(): void {
    this.router.navigate(['/analytics/home']);
  }
}
